import type { FlowError, LoginFlow, RegistrationFlow, UpdateLoginFlowBody } from '@ory/client';
import { DresscodeWebhookTransientPayloadV1Schema, type DresscodeWebhookTransientPayloadV1 } from '@standandforce/api-contract';
import { oryKratosClient } from '../ory-kratos.client';
import { getErrorResponseData, getRedirectBrowserTo, hasHttpStatus, is400Error, isAccountDisabledError, isBrowserLocationChangeRequired, isFlowExpired, type ServiceResponse } from './types';

/**
 * password method で `updateLoginFlow` に投げる body に
 * `transient_payload.used_identifier` を自動付与する。
 *
 * BE の after-login webhook が「ユーザーが業務メアド / 個人メアドのどちらで
 * ログインしたか」を判定するために必須。Kratos の Identity / traits だけでは
 * 両方のメアドを持つユーザーがどちらを使ったかを判別できないため、フォーム
 * 入力値 (`identifier`) を `transient_payload` 経由で BE に再送する。
 *
 * 将来 `identifier` に trim / lowercase 等の正規化を入れる場合は、**同じ処理を
 * `used_identifier` にも適用**すること（BE 側は両フィールドの一致で入力経路を判断する）。
 */
function withTransientPayload(body: UpdateLoginFlowBody): UpdateLoginFlowBody {
  if (body.method !== 'password') return body;
  const existing = body.transient_payload as Partial<DresscodeWebhookTransientPayloadV1> | undefined ?? {};

  // existing を後勝ちで spread することで、不正な値 (例: v が 1 以外) は
  // Schema.parse() で ZodError として throw され、fail-closed で送信を中断する。
  const transient_payload = DresscodeWebhookTransientPayloadV1Schema.parse({
    v: 1,
    used_identifier: body.identifier,
    ...existing
  });
  return {
    ...body,
    transient_payload
  };
}
export const signInService = {
  /**
   * サインインフローを初期化
   *
   * @param options - オプション
   * @param options.refresh - 既存のセッションをリフレッシュするかどうか（パスワード再入力を求める）
   * @param options.aal - 認証保証レベル（"aal1" | "aal2"）。AAL2を指定するとMFA認証フローが初期化されます
   * @returns サインインフロー情報
   *
   * @remarks
   * - `aal: "aal2"` を指定した場合、`refresh` が未指定であれば自動的に `true` に設定されます。
   *   これは既存セッションがある状態でAAL2フローを開始するために必要です。
   * - `refresh: true` を単独で指定すると、パスワード再入力による再認証フローが開始されます。
   *
   * @example
   * ```ts
   * // 通常のサインインフロー
   * const signInFlow = await signInService.initFlow();
   * ```
   *
   * @example
   * ```ts
   * // パスワード再入力による再認証（セッションリフレッシュ）
   * const refreshFlow = await signInService.initFlow({ refresh: true });
   * ```
   *
   * @example
   * ```ts
   * // AAL2（MFA）でログインフローを初期化（自動的にrefresh=trueも設定される）
   * const mfaFlow = await signInService.initFlow({ aal: "aal2" });
   * ```
   *
   * @example
   * ```ts
   * // AAL2（MFA）とrefreshを明示的に指定
   * const mfaRefreshFlow = await signInService.initFlow({ aal: "aal2", refresh: true });
   * ```
   */
  async initFlow(options?: {
    refresh?: boolean;
    aal?: 'aal1' | 'aal2';
  }): Promise<LoginFlow> {
    const params: {
      refresh?: boolean;
      aal?: 'aal1' | 'aal2';
    } = {};
    if (options?.refresh !== undefined) {
      params.refresh = options.refresh;
    }
    if (options?.aal !== undefined) {
      params.aal = options.aal;
      // AAL2フローを開始する際は既存セッションをリフレッシュする必要がある
      // refresh=trueを指定しないと session_already_available エラーが発生する
      if (options.aal === 'aal2' && params.refresh === undefined) {
        params.refresh = true;
      }
    }
    const {
      data
    } = await oryKratosClient.getApi().createBrowserLoginFlow(params);
    if (!data) {
      throw new Error('Failed to create login flow: No data returned');
    }
    return data;
  },
  /**
   * 既存のサインインフローを取得
   *
   * Kratos がリダイレクト時にフローIDをクエリパラメータに付与する場合
   * （例: SSO webhook 失敗時の `/auth/sign-in?flow=xxx`）に使用します。
   * フローデータの `ui.messages` にエラー情報が含まれることがあります。
   *
   * @param flowId - Kratos が発行したログインフローの ID
   * @returns ログインフロー情報
   */
  async getFlow(flowId: string): Promise<LoginFlow> {
    const {
      data
    } = await oryKratosClient.getApi().getLoginFlow({
      id: flowId
    });
    if (!data) {
      throw new Error('Failed to get login flow: No data returned');
    }
    return data;
  },
  /**
   * 既存の Registration フローを取得
   *
   * OIDC (SSO) ログインで Webhook がフローを中断した場合、Kratos は
   * Registration フローを作成し、registration.ui_url（= /auth/sign-in）に
   * リダイレクトする。フローの ui.messages にエラー情報が含まれる。
   *
   * Login フローの取得が 404 の場合にフォールバックとして使用する。
   *
   * @param flowId - Kratos が発行した Registration フローの ID
   * @returns Registration フロー情報
   */
  async getRegistrationFlow(flowId: string): Promise<RegistrationFlow> {
    const {
      data
    } = await oryKratosClient.getApi().getRegistrationFlow({
      id: flowId
    });
    if (!data) {
      throw new Error('Failed to get registration flow: No data returned');
    }
    return data;
  },
  /**
   * Kratos のエラーコンテナからエラー詳細を取得
   *
   * SSO (OIDC) フローで Webhook が失敗した場合など、Kratos がエラーフローを
   * 発生させた際に `/self-service/errors?id={error_id}` からエラー情報を取得します。
   *
   * @param errorId - Kratos が発行したエラーコンテナの ID
   * @returns FlowError オブジェクト（error フィールドに詳細が含まれる）
   *
   * @example
   * ```ts
   * const flowError = await signInService.getFlowError(errorId);
   * const detail = flowError.error as SsoErrorDetail;
   * console.log(detail?.message);
   * ```
   */
  async getFlowError(errorId: string): Promise<FlowError> {
    const {
      data
    } = await oryKratosClient.getApi().getFlowError({
      id: errorId
    });
    if (!data) {
      throw new Error('Failed to get flow error: No data returned');
    }
    return data;
  },
  /**
   * サインインフローを送信（サインイン実行）
   *
   * @param flowId - サインインフローID
   * @param body - サインイン情報（パスワード、メールアドレスなど）
   * @returns サインイン結果。400エラー（バリデーションエラー）の場合は新しいフローデータを返す
   *
   * @remarks
   * password method のとき、`withTransientPayload` 内で `DresscodeWebhookTransientPayloadV1Schema.parse`
   * が失敗すると **このメソッドの try に入る前に `ZodError` が throw** される。
   * これは FE の組み込みミス（`v` 不整合など）を送信前に検知するための fail-closed であり、
   * 通常のユーザー入力バリデーションでは発生しない想定。呼び出し側の catch で握り潰されうるため、
   * 本番では Datadog 等で監視すること。
   *
   * @example
   * ```ts
   * const result = await signInService.submitFlow(flowId, {
   *   password: 'password123',
   *   method: 'password',
   *   password_identifier: 'user@example.com'
   * });
   *
   * if (result.error) {
   *   // 予期せぬエラー（500等）
   *   console.error(result.error);
   * } else if (result.data) {
   *   // 成功またはバリデーションエラー（400）
   *   // result.data.ui を使ってエラーメッセージを表示
   * }
   * ```
   */
  async submitFlow(flowId: string, body: UpdateLoginFlowBody): Promise<ServiceResponse<LoginFlow>> {
    const updateLoginFlowBody = withTransientPayload(body);
    try {
      const {
        data
      } = await oryKratosClient.getApi().updateLoginFlow({
        flow: flowId,
        updateLoginFlowBody
      });

      // updateLoginFlowは成功時にSuccessfulNativeLoginを返すが、
      // バリデーションエラー時はLoginFlowを返す
      // uiプロパティの有無で判定する
      if (data && 'ui' in data) {
        return {
          data: data as unknown as LoginFlow,
          error: null
        };
      }

      // 成功時（SuccessfulNativeLogin）はUIを持たないため、data=nullで扱う
      return {
        data: null,
        error: null
      };
    } catch (err) {
      if (is400Error(err)) {
        const flowData = getErrorResponseData<LoginFlow>(err);
        return {
          data: flowData,
          error: null
        };
      }
      if (isBrowserLocationChangeRequired(err)) {
        const redirectTo = getRedirectBrowserTo(err);
        return {
          data: null,
          error: null,
          redirectTo: redirectTo ?? undefined
        };
      }

      // 利用停止アカウントの 401（identity is disabled）はフラグで呼び出し側に通知する
      if (isAccountDisabledError(err)) {
        return {
          data: null,
          error: null,
          accountDisabled: true
        };
      }
      if (hasHttpStatus(err, 404)) {
        return {
          data: null,
          error: null,
          redirectTo: '/auth/sign-in'
        };
      }

      // self_service_flow_expired (410): 新しいフローを発行して UI 側で再開できるようにする
      if (isFlowExpired(err)) {
        try {
          const newFlow = await signInService.initFlow();
          return {
            data: newFlow,
            error: null,
            flowExpired: true
          };
        } catch (reinitError) {
          // 新規フロー発行に失敗した場合は、元のエラーを優先して返す
          return {
            data: null,
            error: reinitError as Error
          };
        }
      }
      return {
        data: null,
        error: err as Error
      };
    }
  }
};